<template>
    <component
        :is="clickable ? 'button' : 'span'"
        :type="clickable ? 'button' : null"
        class="filter-badge"
        :class="[
            `bdg-tone-${tone}`,
            {
                'filter-badge--clickable': clickable,
                'filter-badge--active': active,
                'filter-badge--muted': mutedSurface,
                'filter-badge--sem-contador': count === null,
                'filter-badge--selecao-tom': toneSelection,
            },
        ]"
        :aria-pressed="clickable ? String(active) : null"
        @click="aoClicar"
    >
        <!-- Marca de seleção do modo caixa-de-seleção: o selo substituiu um
             `<input type="checkbox">`, e sem o "check" o estado ligado ficava só
             na cor — sinal fraco para um controle que muda o menu de ações. -->
        <i v-if="checkable" class="filter-badge__check"
            :class="active ? 'ri-checkbox-line' : 'ri-checkbox-blank-line'"></i>

        <!-- Marca à esquerda: por padrão o ponto colorido do tom. O slot existe
             para quem tem uma marca própria melhor que um ponto — a coluna de
             plataforma passa o `PlataformaBadge` com os ícones das marcas. -->
        <slot name="mark">
            <i v-if="icon" :class="icon" class="filter-badge__icon"></i>
            <span v-else-if="dot" class="filter-badge__dot"></span>
        </slot>

        <span class="filter-badge__label">{{ label }}</span>

        <!-- `count === null` esconde o contador (selo de ação, como o toggle de
             debug). Contador zerado vira travessão: a categoria continua visível
             e o admin vê que ela existe e está vazia, em vez de o selo sumir. -->
        <span
            v-if="count !== null"
            class="filter-badge__count"
            :class="{ 'filter-badge__count--vazio': !count }"
        >
            {{ count > 0 ? count : '—' }}
        </span>
    </component>
</template>

<script>
/**
 * Selo-filtro reutilizável do admin.
 *
 * É indicador e controle ao mesmo tempo: mostra a contagem da categoria e, ao
 * ser clicado, liga/desliga o filtro correspondente. Quem decide a semântica do
 * clique (aditivo dentro do grupo, exclusivo no "Todos") é a página — este
 * componente só reporta o clique e reflete o `active` recebido.
 */
export default {
    name: 'FilterBadge',
    props: {
        label: { type: String, required: true },
        /** `null` = sem contador. `0` = contador vazio (mostra "—"). */
        count: { type: Number, default: null },
        /** Nome do tom da paleta em `assets/css/badges.css` (sem o prefixo). */
        tone: { type: String, default: 'slate' },
        active: { type: Boolean, default: false },
        /** Classe de ícone (remixicon) no lugar do ponto colorido. */
        icon: { type: String, default: '' },
        /** Ponto colorido do tom, quando não há `icon` nem slot `mark`. */
        dot: { type: Boolean, default: true },
        /** Superfície acinzentada — usada nos selos "Todos", que resetam o grupo. */
        mutedSurface: { type: Boolean, default: false },
        clickable: { type: Boolean, default: true },
        /** Mostra uma caixa de seleção à esquerda, marcada conforme `active`. */
        checkable: { type: Boolean, default: false },
        /**
         * Pinta o estado selecionado com o TOM do selo (preenchimento suave,
         * borda mais grossa, texto e ícone na cor do tom) em vez do azul padrão.
         *
         * O azul é o certo para filtro: ele diz "ligado" sem competir com a cor
         * que classifica a categoria. Já um selo que não filtra nada — o toggle
         * de debug — não tem categoria para preservar, e aí vale usar a própria
         * cor para deixar o estado ligado impossível de confundir.
         */
        toneSelection: { type: Boolean, default: false },
    },
    emits: ['click'],
    methods: {
        aoClicar(evento) {
            if (!this.clickable) return;
            this.$emit('click', evento);
        },
    },
};
</script>

<style scoped>
.filter-badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 4px 5px 4px 11px;
    border: 1px solid var(--bdg-border);
    border-radius: 999px;
    background: var(--bdg-surface);
    box-shadow: var(--bdg-shadow);
    color: var(--bdg-fg);
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.35;
    white-space: nowrap;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease,
        background-color 0.15s ease;
}

/* Sem contador o padding da direita volta ao da esquerda: o recuo menor existe
   só para o contador encostar na borda. */
.filter-badge--sem-contador {
    padding-right: 11px;
}

.filter-badge--muted {
    background: var(--bdg-surface-muted);
}

.filter-badge__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--tone);
    flex: 0 0 auto;
}

.filter-badge__icon {
    font-size: 0.95rem;
    line-height: 1;
    color: var(--tone);
}

.filter-badge__label {
    /* O rótulo não encolhe o texto do contador quando o selo é apertado. */
    flex: 0 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
}

.filter-badge__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    padding: 2px 7px;
    border-radius: 999px;
    background: var(--bdg-count-bg);
    color: var(--bdg-count-fg);
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1.25;
    flex: 0 0 auto;
}

/* Categoria existente mas vazia: contador apagado, para o zero não competir
   visualmente com as categorias que têm volume. */
.filter-badge__count--vazio {
    background: var(--bdg-count-empty-bg);
    color: var(--bdg-count-empty-fg);
}

/* ── Interação ─────────────────────────────────────────────────────────────── */

.filter-badge--clickable {
    cursor: pointer;
}

.filter-badge--clickable:hover {
    border-color: var(--bdg-accent);
    box-shadow: var(--bdg-shadow-hover);
    transform: translateY(-1px);
}

.filter-badge--clickable:focus-visible {
    outline: none;
    border-color: var(--bdg-accent);
    box-shadow: 0 0 0 3px var(--bdg-accent-ring);
}

.filter-badge--active {
    border-color: var(--bdg-accent);
    box-shadow: 0 0 0 3px var(--bdg-accent-ring);
}

/* Contador azul = filtro ligado. É o segundo sinal da seleção, para ela não
   depender só da borda (que o hover também pinta). */
.filter-badge--active .filter-badge__count {
    background: var(--bdg-accent);
    color: #fff;
}

.filter-badge--active:hover {
    box-shadow: 0 0 0 3px var(--bdg-accent-ring), var(--bdg-shadow-hover);
}

/* ── Seleção pelo tom (toggle, não filtro) ─────────────────────────────────── */

.filter-badge__check {
    font-size: 0.95rem;
    line-height: 1;
    flex: 0 0 auto;
}

/* A borda mais grossa entra como `box-shadow` interno, e não trocando
   `border-width`: mudar a espessura da borda mexeria na altura do selo e faria
   os vizinhos dançarem a cada clique. */
.filter-badge--selecao-tom.filter-badge--active {
    background: var(--tone-bg);
    border-color: var(--tone);
    box-shadow: inset 0 0 0 1px var(--tone);
    color: var(--tone-fg);
}

.filter-badge--selecao-tom.filter-badge--active:hover {
    box-shadow: inset 0 0 0 1px var(--tone), var(--bdg-shadow-hover);
    border-color: var(--tone);
}

/* Ligado, o ícone acompanha o texto: com o preenchimento do tom no fundo, o
   ícone na cor plena do tom brigava com o rótulo em vez de acompanhá-lo. */
.filter-badge--selecao-tom.filter-badge--active .filter-badge__icon,
.filter-badge--selecao-tom.filter-badge--active .filter-badge__check {
    color: currentColor;
}

.filter-badge--selecao-tom.filter-badge--active .filter-badge__count {
    background: var(--tone);
    color: #fff;
}
</style>
